// Distribution Repository Implementation

import {DistributionRepository} from './DistributionRepository';
import {DistributionAPIDataSource} from '../api/DistributionAPIDataSource';
import {mapDistributionRecordDTOToModel, mapDistributionMarketerDTOToModel, mapDistributionProductDTOToModel, mapAvailableSerialDTOToModel, mapCreateDeliveryFormToDTO} from '../mappers/distribution.mapper';
import {DistributionRecord, DistributionMarketer, DistributionProduct, AvailableSerial, CreateDeliveryForm, UpdateDeliveryForm, DistributionFilters} from '../models/distribution.model';
import {AppError, ErrorType} from '../../../Data/config/AppError';
import {Pagination} from '../../../Data/types/BaseResponse';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';

export class DistributionRepositoryImpl implements DistributionRepository {
  async getDistributionHistory (filters?: DistributionFilters, page: number = 1, limit: number = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE): Promise<{ items: DistributionRecord[]; pagination?: Pagination }> {
    try {
      const filtersDTO = filters ? {
        marketer_id: filters.marketerId ? parseInt(filters.marketerId) : undefined,
        product_id: filters.productId ? parseInt(filters.productId) : undefined,
        status: filters.status,
        date_from: filters.dateFrom,
        date_to: filters.dateTo
      } : undefined;

      const response = await DistributionAPIDataSource.getDistributionHistory(filtersDTO, page, limit);

      // Handle the standard response structure
      const recordsData = response.items || [];

      const mappedRecords = recordsData.map((item: any) => {
        try {
          const mapped = mapDistributionRecordDTOToModel(item);
          return mapped;
        } catch (error) {
          return null;
        }
      }).filter((item): item is DistributionRecord => item !== null) || [];

      return {
        items: mappedRecords,
        pagination: response.pagination
      };
    } catch (error) {
      throw error;
    }
  }

  async getMarketersForDistribution (search?: string): Promise<DistributionMarketer[]> {
    try {
      const response = await DistributionAPIDataSource.getMarketersForDistribution(search);
      return response.map(mapDistributionMarketerDTOToModel);
    } catch (error) {
      throw error;
    }
  }

  async getProductsForDistribution (search?: string): Promise<DistributionProduct[]> {
    try {
      const response = await DistributionAPIDataSource.getProductsForDistribution(search);
      const mappedProducts = response.map(mapDistributionProductDTOToModel);
      return mappedProducts;
    } catch (error) {
      throw error;
    }
  }

  async getAvailableSerials (productId: string): Promise<AvailableSerial[]> {
    try {
      const response = await DistributionAPIDataSource.getAvailableSerialsForProduct(productId);
      return response.map(mapAvailableSerialDTOToModel);
    } catch (error) {
      throw error;
    }
  }

  async getAvailableSerialsForProduct (productId: string): Promise<AvailableSerial[]> {
    try {
      const response = await DistributionAPIDataSource.getAvailableSerialsForProduct(productId);
      return response.map(mapAvailableSerialDTOToModel);
    } catch (error) {
      throw error;
    }
  }

  async createDelivery (deliveryData: CreateDeliveryForm): Promise<{ record: DistributionRecord; message?: string }> {
    try {
      // Get the selected product to determine if it has serials
      const selectedProduct = await this.getProductsForDistribution();
      const product = selectedProduct.find(p => p.id === deliveryData.productId);
      const hasSerial = product?.hasSerial || false;

      // Use the proper mapper that handles delivered_by field
      const deliveryDTO = mapCreateDeliveryFormToDTO(deliveryData, hasSerial);

      const response = await DistributionAPIDataSource.createDelivery(deliveryDTO);
      return {
        record: mapDistributionRecordDTOToModel(response),
        message: 'تم تسليم المنتج للمسوق بنجاح'
      };
    } catch (error) {
      throw error;
    }
  }

  async updateDelivery (id: string, deliveryData: UpdateDeliveryForm): Promise<DistributionRecord> {
    try {
      const deliveryDTO = {
        serial_numbers: deliveryData.serials,
        quantity: deliveryData.quantity,
        notes: deliveryData.notes
      };

      const response = await DistributionAPIDataSource.updateDelivery(id, deliveryDTO);
      return mapDistributionRecordDTOToModel(response);
    } catch (error) {
      throw error;
    }
  }

  async deleteTransaction (transactionId: string): Promise<void> {
    try {
      await DistributionAPIDataSource.deleteTransaction(transactionId);
    } catch (error) {
      throw error;
    }
  }

  async getDeliveryById (): Promise<DistributionRecord> {
    try {
      // This method is not implemented in the DataSource yet
      throw new AppError('Method not implemented', ErrorType.SERVER);
    } catch (error) {
      throw error;
    }
  }

  async getTrackedSerials (filters?: { productId?: string; locationId?: string; status?: string }): Promise<AvailableSerial[]> {
    try {
      const filtersDTO = filters ? {
        product_id: filters.productId,
        location_id: filters.locationId,
        status: filters.status
      } : undefined;

      const response = await DistributionAPIDataSource.getTrackedSerials(filtersDTO);
      return response.map(mapAvailableSerialDTOToModel);
    } catch (error) {
      throw error;
    }
  }
}
